// CreatorDocumentsPanel.tsx — documents d'un créateur, dans l'onglet Ressources de sa fiche
//
// Même présentation que les autres panneaux (barre de filtres, bascule cartes / liste, mêmes
// cartes, même liste). Un document n'a pas d'aperçu : la tuile porte son type (PDF, DOCX…)
// sur une couleur. « Ouvrir » affiche le fichier dans un nouvel onglet.
// Données : admin-api AdminResourceItems (kind = Document).

import React, { useEffect, useMemo, useState } from 'react';

import styles from './ChannelsSection.module.scss';
import extra  from './VideosSection.module.scss';
import { ListTable, TitleCell, WhenCell, RowActions, Dash } from './DashboardList';
import { Segment, ViewToggle, useView, plural, tintOf, size } from './dashboardShared';
import { API_URLS, WWW_URLS } from '../../../backendServices/urls';
import { getAdminResourceItems, AdminResourceItem } from '../../../backendServices/adminManagment/api';

const VIEW_KEY = 'adminResources.documentsView';

type Sort = 'recent' | 'name' | 'size';
const SORTS: readonly (readonly [Sort, string])[] = [ [ 'recent', 'Plus récents' ], [ 'name', 'Nom (A → Z)' ], [ 'size', 'Plus lourds' ] ];
const byDateDesc = (a: AdminResourceItem, b: AdminResourceItem) => (b.date ?? '').localeCompare(a.date ?? '');
const COMPARE: Record<Sort, (a: AdminResourceItem, b: AdminResourceItem) => number> = {
  recent: byDateDesc,
  name  : (a, b) => a.title.localeCompare(b.title, 'fr', { sensitivity: 'base', numeric: true }),
  size  : (a, b) => (b.bytes ?? 0) - (a.bytes ?? 0) || byDateDesc(a, b),
};

const extOf = (d: AdminResourceItem) => {
  const name = d.fileName ?? '';
  return name.includes('.') ? name.split('.').pop()!.toUpperCase() : '';
};

// Même règle que getDocumentUrl (documentManagment/api) : public → serveur de médias, sinon point d'accès privé.
const urlOf = (d: AdminResourceItem) =>
  d.isPublic && d.publicRef && d.fileName
    ? `${WWW_URLS.MediaServer}${d.publicRef}/documents/${d.id}.${d.fileName.includes('.') ? d.fileName.split('.').pop() : 'bin'}`
    : API_URLS.getDocumentFile.replace('{id}', `${d.id}`);

const Pic: React.FC<{ d: AdminResourceItem; mini?: boolean }> = ({ d, mini }) => {
  if (mini) return <div style={{ width: '100%', height: '100%', background: tintOf(d.id), display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 10, fontWeight: 700, color: '#fff' }}>{extOf(d)}</div>;
  return (
    <div className={styles.thumb} style={{ background: tintOf(d.id) }}>
      <span className={styles.thumbNameBig}>{extOf(d) || 'DOC'}</span>
      <span className={styles.badges} style={{ zIndex: 1, position: 'absolute' }}>
        <span className={`${styles.badge} ${d.isPublic ? styles.badgeOn : styles.badgeOff}`}>{d.isPublic ? 'PUBLIC' : 'PRIVÉ'}</span>
      </span>
    </div>
  );
};

const AccessBadge: React.FC<{ d: AdminResourceItem }> = ({ d }) =>
  <span className={`${styles.badge} ${d.isPublic ? styles.badgeOn : styles.badgeOff}`}>{d.isPublic ? 'PUBLIC' : 'PRIVÉ'}</span>;

const Open: React.FC<{ d: AdminResourceItem }> = ({ d }) => (
  <div className={styles.actions}>
    <a className={styles.primary} href={urlOf(d)} target="_blank" rel="noreferrer" style={{ textAlign: 'center', textDecoration: 'none' }}>Ouvrir</a>
  </div>
);

const CreatorDocumentsPanel: React.FC<{ adminId: number }> = ({ adminId }) => {

  const [ docs    , setDocs     ] = useState<AdminResourceItem[]>([]);
  const [ loading , setLoading  ] = useState(true);
  const [ error   , setError    ] = useState(false);
  const [ access  , setAccess   ] = useState<'all' | 'public' | 'private'>('all');
  const [ type    , setType     ] = useState<string>('all');
  const [ query   , setQuery    ] = useState('');
  const [ sort    , setSort     ] = useState<Sort>('recent');
  const [ view    , setView     ] = useView(VIEW_KEY);

  useEffect(() => {
    let cancelled = false;
    setLoading(true); setError(false);
    getAdminResourceItems(adminId, 'Document').then(r => {
      if (cancelled) return;
      if (r.code === 1) setDocs(r.payload.items); else setError(true);
      setLoading(false);
    });
    return () => { cancelled = true; };
  }, [adminId]);

  const types = useMemo(() => {
    const count = new Map<string, number>();
    docs.forEach(d => { const e = extOf(d); if (e) count.set(e, (count.get(e) ?? 0) + 1); });
    return Array.from(count.entries()).sort((a, b) => b[1] - a[1]).slice(0, 5).map(([e]) => e);
  }, [docs]);

  const matching = useMemo(() => {
    const q = query.trim().toLowerCase();
    return docs.filter(d => (type === 'all' || extOf(d) === type) && (!q || `${d.title} ${d.fileName ?? ''}`.toLowerCase().includes(q)));
  }, [docs, type, query]);

  const shown = useMemo(
    () => matching.filter(d => access === 'all' || (access === 'public') === !!d.isPublic).sort(COMPARE[sort]),
    [matching, access, sort]);

  const pub = matching.filter(d => d.isPublic).length;

  if (loading) return <span>Chargement des documents…</span>;
  if (error)   return <div className={styles.empty}>Chargement impossible.</div>;
  if (docs.length === 0) return <div className={styles.empty}>Aucun document.</div>;

  const filtersOn = query.trim() !== '' || access !== 'all' || type !== 'all';

  return (
    <div>
      <div className={styles.toolbar} style={{ marginBottom: 16 }}>
        <div className={extra.search}>
          <svg className={extra.searchIcon} width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true"><circle cx="7" cy="7" r="5"/><path d="M11 11l4 4"/></svg>
          <input className={extra.searchInput} type="text" value={query} placeholder="Titre, fichier…" aria-label="Rechercher un document"
                 onChange={e => setQuery(e.target.value)} />
          {query && <button type="button" className={extra.searchClear} onClick={() => setQuery('')} aria-label="Effacer la recherche">✕</button>}
        </div>
        <Segment label="Accès" value={access} onChange={setAccess}
                 choices={[ ['all', `Tous ${matching.length}`], ['public', `Publics ${pub}`], ['private', `Privés ${matching.length - pub}`] ] as const} />
        {types.length > 1 &&
          <Segment label="Type" value={type} onChange={setType}
                   choices={[ ['all', 'Tous les types'] as const, ...types.map(e => [e, e] as const) ]} />}
        {filtersOn && <>
          <span className={extra.count} aria-live="polite"><strong>{plural(shown.length, 'document')}</strong> sur {docs.length}</span>
          <button type="button" className={extra.reset} onClick={() => { setQuery(''); setAccess('all'); setType('all'); }}>Réinitialiser</button>
        </>}
        <span className={styles.spacer}/>
        <label className={extra.sort}>Trier par
          <select value={sort} onChange={e => setSort(e.target.value as Sort)}>
            {SORTS.map(([k, label]) => <option key={k} value={k}>{label}</option>)}
          </select>
        </label>
        <ViewToggle view={view} onChange={setView} />
      </div>

      {shown.length === 0 && <div className={styles.empty}>Aucun document ne correspond.</div>}

      {view === 'cards' && shown.length > 0 && (
        <div className={styles.cards}>
          {shown.map(d => (
            <article key={d.id} className={styles.card}>
              <Pic d={d} />
              <div className={styles.cardBody}>
                <span className={styles.cardName} title={d.title}>{d.title || '—'}</span>
                <div className={styles.chips}>
                  {extOf(d) && <span>{extOf(d)}</span>}
                  {size(d.bytes ?? 0) && <span>{size(d.bytes ?? 0)}</span>}
                  {d.date && <span>Ajouté le {new Date(d.date).toLocaleDateString('fr-FR')}</span>}
                </div>
                <Open d={d} />
              </div>
            </article>
          ))}
        </div>
      )}

      {view === 'list' && shown.length > 0 && (
        <ListTable items={shown} rowKey={d => d.id} dateOf={d => d.date}
                   noun={{ one: 'document', many: 'documents' }}
                   sort={sort === 'recent' ? null : sort} onSort={id => setSort((id ?? 'recent') as Sort)}
                   columns={[
          { id: 'name', label: 'Document', sort: COMPARE.name, cell: d =>
              <TitleCell thumb={<Pic d={d} mini />} title={d.title || '—'} titleHint={d.title} sub={d.fileName ?? ''} /> },
          { id: 'access', label: 'Accès', cell: d => <AccessBadge d={d} /> },
          { id: 'type', label: 'Type', cell: d => extOf(d) || <Dash/> },
          { id: 'size', label: 'Taille', right: true, sort: COMPARE.size, cell: d => size(d.bytes ?? 0) || <Dash/> },
          { id: 'date', label: 'Ajouté', cell: d => <WhenCell iso={d.date} /> },
          { id: 'actions', label: '', right: true, cell: d => <RowActions><Open d={d} /></RowActions> },
        ]} />
      )}
    </div>
  );
};

export default CreatorDocumentsPanel;
